import { isAxiosErrorProblemDetails } from "../../../api";
import type { ProblemDetails, TokenViewModel } from "../../../api/axcloud";
import type { RootState } from "../../../app/rootReducer";
import { isCurrentTwoFactorRequest } from "../state/loginState";

/**
 * エラーオブジェクトからProblemDetailsを取得します。
 * @param error 取得するエラーオブジェクト
 * @returns ProblemDetailsが取得できる場合はProblemDetailsを返し、取得できない場合はnullを返します。
 */
export function getProblemDetails(error: unknown): ProblemDetails | null {
  if (!isAxiosErrorProblemDetails(error)) {
    return null;
  }

  return error.response?.data ?? null;
}

export interface TwoFactorThunkState {
  getState: () => RootState;
  requestId: string;
}

/**
 * 二要素認証リクエストがアクティブであるかどうかを判定します。
 * @param state Reduxの状態
 * @param requestId 二要素認証リクエストID
 * @param accessToken 二要素認証リクエストに関連付けられたアクセストークン
 * @returns ログイン状態が二要素認証が必要かつ指定されたリクエストIDが現在のリクエストIDと一致し、かつ指定されたアクセストークンが現在の保留中のアクセストークンと一致する場合にtrueを返します。
 */
export function isActiveTwoFactorRequest(
  state: RootState,
  requestId: string,
  accessToken: string
): boolean {
  return (
    isCurrentTwoFactorRequest(state.login, requestId) &&
    state.login.pendingAccessToken === accessToken
  );
}

type TwoFactorResult =
  | {
      ok: true;
      token: TokenViewModel;
    }
  | {
      ok: false;
      problem: ProblemDetails;
    };

/**
 * 二要素認証の完了を試みます。
 * @param thunk 二要素認証の状態を持つThunk
 * @param accessToken 二要素認証リクエストに関連付けられたアクセストークン
 * @param request 二要素認証を完了するためのリクエスト関数
 * @param cancelledProblem 二要素認証がキャンセルされた場合に返すProblemDetails
 * @returns 二要素認証の結果
 */
export async function completeTwoFactorLogin(
  thunk: TwoFactorThunkState,
  accessToken: string,
  request: () => Promise<{ data: TokenViewModel }>,
  cancelledProblem: ProblemDetails
): Promise<TwoFactorResult> {
  // 二要素認証のリクエストが有効でない場合は、キャンセルされたとみなしてProblemDetailsを返す
  if (!isActiveTwoFactorRequest(thunk.getState(), thunk.requestId, accessToken)) {
    return {
      ok: false,
      problem: cancelledProblem,
    };
  }

  try {
    const response = await request();
    // リクエストが無効になっていないかチェック
    if (!isActiveTwoFactorRequest(thunk.getState(), thunk.requestId, accessToken)) {
      return {
        ok: false,
        problem: cancelledProblem,
      };
    }

    return {
      ok: true,
      token: response.data,
    };
  } catch (error) {
    const problem = getProblemDetails(error);
    if (problem) {
      return {
        ok: false,
        problem,
      };
    }
    throw error;
  }
}
